import BBAnalytics from '../../services/BBAnalytics';
import Features from '../../services/Features';
import MoneyUtils from '../../services/MoneyUtils';
import { upsellPricing } from '../../services/upsellAddOns';

// The five moments this arm reports for itself. docs/analytics-events.md has
// the full map and the reasoning behind each choice.

// Where legacy fires it: the page having a cart, not the drawer opening.
export const reportCheckoutOpened = (cartInfo) => {
  if (!cartInfo) return false;
  BBAnalytics.checkoutStarted(cartInfo);
  return true;
};

/* Legacy's step numbers, mapped by meaning rather than position — it asks for
   shipping before customization and this checkout does the reverse. */
const STEP_EVENT = {
  shipping: 2, // add_shipping_info
  customization: 3, // add_customization_info
  payment: 4, // add_payment_info
};

export const reportStepCompleted = (step, cartInfo) => {
  const stepNumber = STEP_EVENT[step];
  if (!stepNumber || !cartInfo) return false;
  BBAnalytics.checkoutProcess(cartInfo, stepNumber);
  return true;
};

/* Legacy reports this as GA view_promotion with these exact strings
   (thank-you/index.js:246-258) — it re-fires on every render of its list, this
   reports once per opening.

   The place is a parameter because the offer moved: the same products are
   shown in the cart drawer before the order now, and reporting that under
   'Post Purchase' would be an impression GA could not tell from one shown
   after a charge. The name matches the list the add is attributed to, so the
   two halves report the same promotion. Legacy's own string stays the default
   so the thank-you page keeps reporting what it always did. */
export const POST_PURCHASE = 'Post Purchase';
export const CART_DRAWER = 'Cart';

// Legacy's own strings for each list (thank-you/index.js:251, review/index.js:163).
const PROMO = {
  [POST_PURCHASE]: {
    id: 'Post Purchase',
    name: 'Post Purchase Upsell',
    position: 'Post Purchase',
  },
  [CART_DRAWER]: {
    id: 'Cart Upsell',
    name: 'Cart Upsell',
    position: 'Cart Review',
  },
};

export const reportOfferShown = (offers, place = POST_PURCHASE) => {
  if (!offers?.length) return false;
  BBAnalytics.viewPromo({ ...PROMO[place], products: offers });
  // Attach rate needs a denominator Pulse can read, and GA's view_promotion
  // above is not one.
  Features.logEvent({
    eventName: 'welcomeflow_upsell_presented',
    metadata: { placement: place, checkoutVersion: 'unified' },
  });
  return true;
};

/* The welcome flow's own add event (welcome-flow/index.js:534), reused rather
   than replaced: the same `product` key means one metric scores both arms.
   `placement` separates this arm's two surfaces; legacy fires it without one. */
export const reportAddOnAdded = (product, place) => {
  if (!product) return false;
  Features.logEvent({
    eventName: 'welcomeflow_upsell_added',
    metadata: {
      product: product._id,
      placement: place,
      checkoutVersion: 'unified',
    },
  });
  return true;
};

/* Legacy fires this as an add-on is picked: 'Cart Review' from the cart
   (review/index.js:105) and no position after the order (thank-you/index.js:79). */
export const reportAddOnViewed = (product, position) => {
  if (!product) return false;
  BBAnalytics.viewedProductEvent(product, true, position);
  return true;
};

/* Legacy's second purchase for the add-ons, copied as is
   (thank-you/index.js:158-192): the totals are the whole capture's and `res`
   keeps its subscription, so the subscription events fire again. Only the
   lines and the overall total on the cart are the add-ons'. */
export const reportAddOnPurchase = (res, chosen, paymentData) => {
  if (!res?.subCosts || !chosen?.length) return false;
  const addOnTotal = chosen.reduce(
    (sum, product) => upsellPricing(product).cost + sum,
    0,
  );
  const eventRes = {
    ...res,
    subCosts: { ...res.subCosts, overallTotal: addOnTotal },
  };
  const addOnCart = {
    ...eventRes,
    subscription: null,
    productsAndDetails: chosen.map((product) => ({
      quantity: 1,
      promoItem: true,
      color: 'NONE',
      variation: 'NONE',
      product: { ...product },
    })),
  };
  BBAnalytics.purchaseEvent(
    {
      res: eventRes,
      subTotal: MoneyUtils.formatAsMoney(res.subCosts.subTotal),
      total: MoneyUtils.formatAsMoney(res.subCosts.overallTotal),
      taxesAndFees: MoneyUtils.formatAsMoney(res.subCosts.taxesAndFees),
      shipping: MoneyUtils.formatAsMoney(res.subCosts.shipping),
      paymentType: paymentData?.paymentType,
      isPostPurchasePurchase: true,
    },
    addOnCart,
  );
  return true;
};

export const reportAddOnRemoved = (product, place) => {
  if (!product) return false;
  Features.logEvent({
    eventName: 'welcomeflow_upsell_removed',
    metadata: {
      product: product._id,
      placement: place,
      checkoutVersion: 'unified',
    },
  });
  return true;
};

/* Legacy's pair off one exit: declined only when nothing was taken, exit
   always (welcome-flow/index.js:596-601). Post purchase only — the drawer is a
   block on a page, not an offer you leave. */
const offerExitEvent = (eventName) => {
  Features.logEvent({
    eventName,
    metadata: { placement: POST_PURCHASE, checkoutVersion: 'unified' },
  });
  return true;
};

export const reportOfferDeclined = () =>
  offerExitEvent('welcomeflow_upsell_declined');

export const reportOfferExited = () => offerExitEvent('welcomeflow_upsell_exit');

/* The 2024 arm's upgrade events, reused for the drawer's modal. A third
   premiumVariant because the existing two name the two 2024 surfaces, and no
   freeOffer: its experiment has ended, so that field reads false for everyone
   over there while the free month is in fact live (services/premiumUpgrade.js:11). */
const IN_DRAWER = 'inDrawer';

const upgradeEvent = (eventName) => {
  Features.logEvent({
    eventName,
    /* checkoutVersion, the key the shared purchase event already tags the arm
       with (checkoutUtils.js:550) — one key across every Statsig event here.
       Metadata only: the identically named field on paymentData is transmitted
       and must never carry this (see flattenPaymentState). */
    metadata: { premiumVariant: IN_DRAWER, checkoutVersion: 'unified' },
  });
  return true;
};

export const reportUpgradeShown = () =>
  upgradeEvent('premium_upgrade_pageViewed');

export const reportUpgradeSelected = () =>
  upgradeEvent('premium_upgrade_selected');

export const reportUpgradeDeclined = () =>
  upgradeEvent('premium_upgrade_declined');

// phoneNumber is the key TikTok's identify reads.
export const reportPaymentSubmitted = ({ email, phone }) => {
  BBAnalytics.paymentInfoSubmitted({ email, phoneNumber: phone });
};

/* checkoutUtils fires this off cartInfo.babyBirthday (checkoutUtils.js:536),
   which is null here — only addSubscriptionDetails sets it — so this reports
   the date the payload sent. Its month is 1-indexed for the server. */
export const reportDueDate = ({ babyBirthday, email, babyGender }) => {
  if (!babyBirthday || !email) return false;

  const { day, month, year } = babyBirthday;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return false;

  /* The gift-options answer rides along rather than getting its own identify:
     it is the same profile, stamped at the same moment, and gender only reaches
     Klaviyo as an event property today — on seven subscription events a new
     customer has none of. */
  BBAnalytics.setDueDateEvent(date, email, undefined, babyGender);
  return true;
};

/* Legacy's review page reports every code applied or removed this way, with
   whatever came back, a rejected code included (review/index.js:165, :310).
   Copied as is. */
export const reportCartUpdated = (cart) => {
  BBAnalytics.updatedCartEvent(cart);
  return true;
};

/* Unified-only telemetry: where shoppers get stuck, by section rather than by
   field, so a dip in conversion can be traced before anyone rolls back.
   Metadata values are strings; nothing the shopper typed goes in. */
const telemetry = (eventName, metadata = {}) => {
  Features.logEvent({
    eventName,
    metadata: { ...metadata, checkoutVersion: 'unified' },
  });
  return true;
};

export const reportCheckoutViewed = () => telemetry('checkout_viewed');

export const reportSectionCompleted = (section) =>
  section ? telemetry('checkout_section_completed', { section }) : false;

// One flag per section as well as the list, so a metric can filter on equality.
export const reportSubmitBlocked = ({ missing = [], attempt = 1 }) => {
  if (!missing.length) return false;
  return telemetry('checkout_submit_blocked', {
    missing: missing.join(','),
    missingAccount: String(missing.includes('account')),
    missingShipping: String(missing.includes('shipping')),
    missingPayment: String(missing.includes('payment')),
    missingDueDate: String(missing.includes('dueDate')),
    attempt: String(attempt),
  });
};

export const REJECTION = {
  ACCOUNT: 'account',
  PAYMENT: 'payment',
  CART: 'cart',
  SERVER: 'server',
};

export const reportOrderRejected = (reason) =>
  reason ? telemetry('checkout_order_rejected', { reason }) : false;

export const reportOrderPlaced = ({ isSub }) =>
  telemetry('checkout_order_placed', { isSub: String(!!isSub) });

export default {
  reportAddOnAdded,
  reportAddOnPurchase,
  reportAddOnViewed,
  reportAddOnRemoved,
  reportCartUpdated,
  reportCheckoutViewed,
  reportOrderPlaced,
  reportOrderRejected,
  reportSectionCompleted,
  reportSubmitBlocked,
  reportOfferDeclined,
  reportOfferExited,
  reportUpgradeDeclined,
  reportUpgradeSelected,
  reportUpgradeShown,
  reportCheckoutOpened,
  reportStepCompleted,
  reportOfferShown,
  reportPaymentSubmitted,
  reportDueDate,
};
